import { motion } from 'framer-motion'
import { Users, Shield, Lock, Code } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import { useWaitlist } from '@/context/WaitlistContext'
import { useAnimateOnScroll } from '@/hooks/useAnimateOnScroll'
import { Counter } from '@/components/ui/Counter'
import { Badge } from '@/components/ui/Badge'
import { MapleLeaf } from '@/components/ui/MapleLeaf'

const trustIcons = {
  maple: MapleLeaf,
  shield: Shield,
  lock: Lock,
}

export function SocialProof() {
  const { t } = useLanguage()
  const { count } = useWaitlist()
  const { ref, isVisible } = useAnimateOnScroll({ threshold: 0.2 })

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-midnight-950 via-midnight-900 to-midnight-950" />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-100 mb-4">
            {t.socialProof.headline}
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t.socialProof.subheadline}
          </p>
        </motion.div>

        {/* Live Counter Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isVisible ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-lg mx-auto mb-16"
        >
          <div className="glass-card p-8 text-center relative overflow-hidden">
            {/* Animated Border */}
            <div className="absolute inset-0 rounded-2xl">
              <div
                className="absolute inset-0 rounded-2xl opacity-50"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(247, 208, 70, 0.3), transparent)',
                  animation: 'shimmer 3s linear infinite',
                  backgroundSize: '200% 100%',
                }}
              />
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Users className="w-6 h-6 text-gold-400" />
                <span className="text-slate-400">{t.socialProof.counter.label}</span>
              </div>

              <div className="text-6xl font-bold mb-4">
                <Counter value={count} className="gradient-text" />
              </div>

              <p className="text-slate-400">
                {t.socialProof.counter.cta.replace('{count}', (count + 1).toString())}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Transparency Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-center mb-12"
        >
          <div className="glass-card px-6 py-4 inline-flex items-center gap-4">
            <Badge variant="gold" className="gap-2">
              <Code className="w-4 h-4" />
              {t.socialProof.transparency.badge}
            </Badge>
            <p className="text-slate-400 text-sm">
              {t.socialProof.transparency.text}
            </p>
          </div>
        </motion.div>

        {/* Trust Signals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-wrap justify-center gap-6"
        >
          {t.socialProof.trust.map((item, index) => {
            const Icon = trustIcons[item.icon as keyof typeof trustIcons]
            return (
              <div
                key={index}
                className="trust-badge"
              >
                <Icon
                  className={`w-5 h-5 ${
                    item.icon === 'maple' ? 'text-maple-500' : 'text-gold-400'
                  }`}
                />
                <span>{item.text}</span>
              </div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}
